import { create } from 'zustand';
import {
  getProjects,
  getMyProjects,
  getProjectById,
  createProject as apiCreateProject,
  updateProject as apiUpdateProject,
  deleteProject as apiDeleteProject,
  evaluateProject as apiEvaluateProject,
} from '../services/api';

const useProjectStore = create((set, get) => ({
  // ── Faculty projects (all students) ──
  projects: [],
  loadingProjects: false,
  errorProjects: null,

  // ── Student projects (my projects) ──
  myProjects: [],
  loadingMyProjects: false,
  errorMyProjects: null,

  // ── Project details cache ──
  projectById: {}, // { [id]: project }
  loadingProjectById: {}, // { [id]: boolean }
  errorProjectById: {}, // { [id]: Error | string }

  // ── Mutation state ──
  creatingProject: false,
  createError: null,
  updatingProject: {}, // { [id]: boolean }
  updateError: {}, // { [id]: Error | string }
  deletingProject: {}, // { [id]: boolean }
  deleteError: {}, // { [id]: Error | string }
  evaluatingProject: {}, // { [id]: boolean }
  evaluateError: {}, // { [id]: Error | string }

  // ── Actions ──
  async fetchProjects() {
    set({ loadingProjects: true, errorProjects: null });
    try {
      const res = await getProjects();
      const projects = res.data?.projects || [];
      set({ projects, loadingProjects: false });
      return projects;
    } catch (err) {
      set({ errorProjects: err, loadingProjects: false });
      throw err;
    }
  },

  async fetchMyProjects() {
    set({ loadingMyProjects: true, errorMyProjects: null });
    try {
      const res = await getMyProjects();
      const myProjects = res.data?.projects || [];
      set({ myProjects, loadingMyProjects: false });
      return myProjects;
    } catch (err) {
      set({ errorMyProjects: err, loadingMyProjects: false });
      throw err;
    }
  },

  async fetchProjectById(id) {
    set((state) => ({
      loadingProjectById: { ...state.loadingProjectById, [id]: true },
      errorProjectById: { ...state.errorProjectById, [id]: null },
    }));
    try {
      const res = await getProjectById(id);
      const project = res.data?.project;
      set((state) => ({
        projectById: { ...state.projectById, [id]: project },
        loadingProjectById: { ...state.loadingProjectById, [id]: false },
      }));
      return project;
    } catch (err) {
      set((state) => ({
        errorProjectById: { ...state.errorProjectById, [id]: err },
        loadingProjectById: { ...state.loadingProjectById, [id]: false },
      }));
      throw err;
    }
  },

  async createProject(data) {
    set({ creatingProject: true, createError: null });
    try {
      const res = await apiCreateProject(data);
      const created = res.data?.project || res.data;
      // Optimistically add to myProjects list if present
      set((state) => ({
        creatingProject: false,
        myProjects: state.myProjects ? [created, ...state.myProjects] : [created],
      }));
      // Refresh lists to ensure consistency
      try { await get().fetchProjects(); } catch { /* empty */ }
      try { await get().fetchMyProjects(); } catch {
        /* empty */
      }
      return created;
    } catch (err) {
      set({ creatingProject: false, createError: err });
      throw err;
    }
  },

  async updateProject(id, data) {
    set((state) => ({
      updatingProject: { ...state.updatingProject, [id]: true },
      updateError: { ...state.updateError, [id]: null },
    }));
    try {
      const res = await apiUpdateProject(id, data);
      const updated = res.data?.project || res.data;
      set((state) => ({
        updatingProject: { ...state.updatingProject, [id]: false },
        projectById: { ...state.projectById, [id]: updated },
        // Update in lists if present
        projects: state.projects.map((p) => (p._id === id ? updated : p)),
        myProjects: state.myProjects.map((p) => (p._id === id ? updated : p)),
      }));
      // Refresh lists
      try { await get().fetchProjects(); } catch { /* empty */ }
      try { await get().fetchMyProjects(); } catch { /* empty */ }
      return updated;
    } catch (err) {
      set((state) => ({
        updatingProject: { ...state.updatingProject, [id]: false },
        updateError: { ...state.updateError, [id]: err },
      }));
      throw err;
    }
  },

  async deleteProject(id) {
    set((state) => ({
      deletingProject: { ...state.deletingProject, [id]: true },
      deleteError: { ...state.deleteError, [id]: null },
    }));
    try {
      await apiDeleteProject(id);
      set((state) => {
        const { [id]: _, ...restById } = state.projectById;
        const { [id]: _l, ...restLoading } = state.loadingProjectById;
        const { [id]: _e, ...restError } = state.errorProjectById;
        return {
          deletingProject: { ...state.deletingProject, [id]: false },
          projectById: restById,
          loadingProjectById: restLoading,
          errorProjectById: restError,
          projects: state.projects.filter((p) => p._id !== id),
          myProjects: state.myProjects.filter((p) => p._id !== id),
        };
      });
      // Refresh lists
      try { await get().fetchProjects(); } catch { /* empty */ }
      try { await get().fetchMyProjects(); } catch { /* empty */ }
      return true;
    } catch (err) {
      set((state) => ({
        deletingProject: { ...state.deletingProject, [id]: false },
        deleteError: { ...state.deleteError, [id]: err },
      }));
      throw err;
    }
  },

  async evaluateProject(id, evaluation) {
    set((state) => ({
      evaluatingProject: { ...state.evaluatingProject, [id]: true },
      evaluateError: { ...state.evaluateError, [id]: null },
    }));
    try {
      const res = await apiEvaluateProject(id, evaluation);
      const updated = res.data?.project || res.data;
      set((state) => ({
        evaluatingProject: { ...state.evaluatingProject, [id]: false },
        projectById: { ...state.projectById, [id]: updated },
        projects: state.projects.map((p) => (p._id === id ? updated : p)),
        myProjects: state.myProjects.map((p) => (p._id === id ? updated : p)),
      }));
      // Refresh lists
      try { await get().fetchProjects(); } catch { /* empty */ }
      try { await get().fetchMyProjects(); } catch { /* empty */ }
      return updated;
    } catch (err) {
      set((state) => ({
        evaluatingProject: { ...state.evaluatingProject, [id]: false },
        evaluateError: { ...state.evaluateError, [id]: err },
      }));
      throw err;
    }
  },
}));

export default useProjectStore;
